import { useEffect, useMemo, useRef, useState } from 'react';
import classNames from './BulkProgress.module.scss';
import {
  BulkOperationResponseData,
  BulkOperationState,
} from '@/school-management/types/services/bulk';
import { bulkService } from '@/school-management/services/bulk';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';

const GET_BULK_OPERATION_TIMER = 2000;
const FINISH_UI_DELAY_MS = GET_BULK_OPERATION_TIMER;

interface BulkProgressProps {
  operationId: string;
  onFinish: (bulkOperation: BulkOperationResponseData) => void;
}

const BulkProgress = ({ operationId, onFinish }: BulkProgressProps) => {
  const { user } = useAppSelector(authSelector);
  const [bulkOperation, setBulkOperation] = useState<BulkOperationResponseData>();
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  const isOperationFinish = (bulkOperation: BulkOperationResponseData | null = null) => {
    switch (bulkOperation?.state) {
      case BulkOperationState.Completed:
      case BulkOperationState.PartialSuccess:
      case BulkOperationState.Failed:
        return true;
    }

    return false;
  };

  useEffect(() => {
    let isActive = true;
    let timer: any = null;
    const organizationId = user?.metadata?.organizationDetails?.organizationId || '';

    setBulkOperation(undefined);

    const getBulkOperation = async () => {
      let bulkOperation: BulkOperationResponseData | null = null;

      try {
        const bulkOperationResponse = await bulkService.getBulkOperation(
          organizationId,
          operationId
        );

        if (bulkOperationResponse.data?.data) {
          bulkOperation = bulkOperationResponse.data.data;
        }
      } catch (e) {}

      if (!isActive) {
        return;
      }

      if (bulkOperation) {
        setBulkOperation(bulkOperation);
      }

      if (!isOperationFinish(bulkOperation)) {
        timer = setTimeout(getBulkOperation, GET_BULK_OPERATION_TIMER);
      }
    };

    if (operationId) {
      getBulkOperation();
    }

    return () => {
      isActive = false;

      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [operationId]);

  useEffect(() => {
    if (!bulkOperation || !isOperationFinish(bulkOperation)) {
      return;
    }

    const op = bulkOperation;
    if (op.state === BulkOperationState.Failed) {
      onFinishRef.current(op);
      return;
    }

    const timer = setTimeout(() => {
      onFinishRef.current(op);
    }, FINISH_UI_DELAY_MS);

    return () => clearTimeout(timer);
  }, [bulkOperation]);

  const progress = useMemo(() => {
    if (!bulkOperation) {
      return 0;
    }
    if (
      bulkOperation.state === BulkOperationState.Completed ||
      bulkOperation.state === BulkOperationState.PartialSuccess
    ) {
      return 100;
    }
    if (!bulkOperation.totalItems) {
      return 0;
    }

    return Math.floor(((bulkOperation.results || []).length * 100) / bulkOperation.totalItems);
  }, [bulkOperation]);

  return (
    <div className={classNames.wrap}>
      <div
        className={classNames.progressContainer}
        style={
          {
            '--percentage': progress,
          } as any
        }
      >
        <svg className={classNames.circularLoader} viewBox='0 0 100 100'>
          <circle className={classNames.loaderBg} cx='50' cy='50' r='45'></circle>
          <circle className={classNames.loaderProgress} cx='50' cy='50' r='45'></circle>
        </svg>
        <div className={classNames.progressText}>{progress}%</div>
      </div>
    </div>
  );
};

export default BulkProgress;
